import { createGLTransaction, GLTransaction, GLTransactionEntry, getGLTransactions, getGLTransactionById } from '../firebase/glTransactionServices';
import { getGLAccountByNumber } from '../firebase/glAccountServices';
import { Timestamp, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { format, endOfMonth, startOfMonth } from 'date-fns';
import { Commission, getCommissions } from '../firebase/financeServices';
import { db } from '../firebase';

/**
 * Helper function to determine if a commission is for a service or product
 * Checks both services and products collections
 */
const determineCategory = async (serviceName: string, serviceId?: string): Promise<string> => {
  if (!serviceId) return 'undefined';
  
  try {
    // First check if the serviceId exists in the services collection
    const serviceDoc = doc(db, 'services', serviceId);
    const serviceSnapshot = await getDoc(serviceDoc);
    
    if (serviceSnapshot.exists()) {
      return 'service';
    }
    
    // If not found in services, check the products collection
    const productDoc = doc(db, 'products', serviceId);
    const productSnapshot = await getDoc(productDoc);
    
    if (productSnapshot.exists()) {
      return 'product';
    }
    
    // If not found in either collection, make a best guess based on name
    const name = serviceName.toLowerCase();
    
    // Check if it's a service based on common service keywords
    if (
      name.includes('service') || 
      name.includes('treatment') || 
      name.includes('massage') || 
      name.includes('facial') || 
      name.includes('therapy') ||
      name.includes('cut') ||
      name.includes('style') ||
      name.includes('color') ||
      name.includes('hair')
    ) {
      return 'service';
    }
    
    // Check if it's a product based on common product keywords
    if (
      name.includes('product') ||
      name.includes('shampoo') ||
      name.includes('conditioner') ||
      name.includes('lotion') ||
      name.includes('cream') ||
      name.includes('oil') ||
      name.includes('package')
    ) {
      return 'product';
    }
    
    return 'undefined';
  } catch (error) {
    console.error('Error determining category:', error);
    return 'undefined';
  }
};

// Interface for monthly commission data
export interface MonthlyCommissionData {
  year: number;
  month: number;
  serviceCommission: number;
  productCommission: number;
  totalCommission: number;
  staffCount: number;
  notes?: string;
}

// Function to fetch commission data for a specific month
export const fetchMonthlyCommissionData = async (year: number, month: number): Promise<MonthlyCommissionData> => {
  try {
    // Create date range for the selected month
    const startDate = startOfMonth(new Date(year, month));
    const endDate = endOfMonth(new Date(year, month));
    
    // Get commissions from Firebase
    const commissionsData = await getCommissions(undefined, startDate, endDate);
    
    // Filter to only include commission splits (type='commission' or undefined for backward compatibility)
    const commissionData = commissionsData.filter(comm => comm.type === 'commission' || !comm.type);
    
    // Calculate totals
    let serviceCommission = 0;
    let productCommission = 0;
    let totalCommission = 0;
    const staffIds = new Set<string>();
    
    // Process each commission and categorize them
    const processCommissions = async () => {
      const categoryPromises = commissionData.map(async (commission) => {
        // Add to staff count
        staffIds.add(commission.staffId);
        
        // Only process pending commissions (not paid or voided)
        if (commission.status !== 'pending') {
          return { amount: 0, category: null };
        }
        
        // Determine category by checking Firestore collections
        let category = 'undefined';
        
        if (commission.serviceId) {
          try {
            // First check if the serviceId exists in the services collection
            const serviceDoc = doc(db, 'services', commission.serviceId);
            const serviceSnapshot = await getDoc(serviceDoc);
            
            if (serviceSnapshot.exists()) {
              category = 'service';
            } else {
              // If not found in services, check the products collection
              const productDoc = doc(db, 'products', commission.serviceId);
              const productSnapshot = await getDoc(productDoc);
              
              if (productSnapshot.exists()) {
                category = 'product';
              }
            }
          } catch (error) {
            console.error('Error checking service/product collections:', error);
            // Fall back to name-based determination
            category = await determineCategory(commission.serviceName, commission.serviceId);
          }
        } else {
          // If no serviceId, fall back to name-based determination
          category = await determineCategory(commission.serviceName);
        }
        
        return { 
          amount: commission.amount,
          category
        };
      });
      
      const results = await Promise.all(categoryPromises);
      
      // Calculate totals based on categories
      results.forEach(result => {
        if (!result.category) return;
        
        totalCommission += result.amount;
        
        if (result.category === 'service') {
          serviceCommission += result.amount;
        } else if (result.category === 'product') {
          productCommission += result.amount;
        } else {
          // If category is undefined, split 50/50
          serviceCommission += result.amount / 2;
          productCommission += result.amount / 2;
        }
      });
    };
    
    await processCommissions();
    
    return {
      year,
      month,
      serviceCommission,
      productCommission,
      totalCommission,
      staffCount: staffIds.size,
      notes: `Monthly commission for ${format(startDate, 'MMMM yyyy')}`
    };
  } catch (error) {
    console.error('Error fetching monthly commission data:', error);
    throw error;
  }
}

// Interface for GL entry preview
export interface GLEntryPreview {
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
  accountId?: string;
}

// GL Account numbers for commission entries
const COMMISSION_GL_ACCOUNTS = {
  'serviceCommission': { accountNumber: '7200001', name: 'Services Commission' },
  'productCommission': { accountNumber: '7200002', name: 'Product Commission' },
  'commissionAccrual': { accountNumber: '3100001', name: 'Accrual-Commission' }
};

/**
 * Generate GL entry previews for monthly commission
 * 
 * @param commissionData Monthly commission data
 * @returns Array of GL entry previews
 */
export const generateCommissionGLEntryPreviews = (commissionData: MonthlyCommissionData): GLEntryPreview[] => {
  const entries: GLEntryPreview[] = [];
  
  // Add debit entries for commission expenses
  if (commissionData.serviceCommission > 0) {
    entries.push({
      accountNumber: COMMISSION_GL_ACCOUNTS.serviceCommission.accountNumber,
      accountName: COMMISSION_GL_ACCOUNTS.serviceCommission.name,
      debit: commissionData.serviceCommission,
      credit: 0
    });
  }
  
  if (commissionData.productCommission > 0) {
    entries.push({
      accountNumber: COMMISSION_GL_ACCOUNTS.productCommission.accountNumber,
      accountName: COMMISSION_GL_ACCOUNTS.productCommission.name,
      debit: commissionData.productCommission,
      credit: 0
    });
  }
  
  // Add credit entry for commission accrual
  if (commissionData.totalCommission > 0) {
    entries.push({
      accountNumber: COMMISSION_GL_ACCOUNTS.commissionAccrual.accountNumber,
      accountName: COMMISSION_GL_ACCOUNTS.commissionAccrual.name,
      debit: 0,
      credit: commissionData.totalCommission
    });
  }
  
  return entries;
};

/**
 * Create GL transaction from monthly commission data
 * 
 * @param commissionData Monthly commission data
 * @param glEntryPreviews GL entry previews (optional - if not provided, will generate from commissionData)
 * @returns Promise with the created GL transaction ID
 */
export const createMonthlyCommissionGLTransaction = async (
  commissionData: MonthlyCommissionData,
  glEntryPreviews?: GLEntryPreview[]
): Promise<string> => {
  try {
    // If no previews provided, generate them
    const entries = glEntryPreviews || generateCommissionGLEntryPreviews(commissionData);
    
    // Validate that debits = credits
    const totalDebits = entries.reduce((sum, entry) => sum + entry.debit, 0);
    const totalCredits = entries.reduce((sum, entry) => sum + entry.credit, 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      throw new Error('GL entries are not balanced. Total debits must equal total credits.');
    }
    
    // Convert entry previews to GL transaction entries
    const glEntries: GLTransactionEntry[] = [];
    
    for (const entry of entries) {
      // Get account ID by account number
      let accountId: string | undefined = entry.accountId;
      
      if (!accountId) {
        // Find the account by account number
        const account = await getGLAccountByNumber(entry.accountNumber);
        if (!account) {
          throw new Error(`GL Account with number ${entry.accountNumber} not found`);
        }
        accountId = account.id;
      }
      
      if (!accountId) {
        throw new Error(`Could not find GL Account ID for account number ${entry.accountNumber}`);
      }
      
      glEntries.push({
        accountId,
        accountNumber: entry.accountNumber,
        accountName: entry.accountName,
        debit: entry.debit,
        credit: entry.credit
      });
    }
    
    // Create the GL transaction
    const transactionDate = endOfMonth(new Date(commissionData.year, commissionData.month, 1));
    const monthName = format(transactionDate, 'MMMM yyyy');
    
    const glTransaction: Omit<GLTransaction, 'id'> = {
      transactionDate,
      description: `Monthly Commission - ${monthName}`,
      referenceType: 'monthly_commission',
      referenceId: `${commissionData.year}-${commissionData.month + 1}`, // Use year-month as reference ID
      entries: glEntries
    };
    
    // Create the transaction in Firestore
    const transactionId = await createGLTransaction(glTransaction);
    
    // Save the transaction ID to the monthly closing document
    const monthlyClosingId = `${commissionData.year}-${commissionData.month + 1}`;
    const monthlyClosingRef = doc(db, 'monthlyClosings', monthlyClosingId);
    
    // Check if the monthly closing document exists
    const monthlyClosingDoc = await getDoc(monthlyClosingRef);
    
    if (monthlyClosingDoc.exists()) {
      // Update the existing document
      await updateDoc(monthlyClosingRef, {
        glTransactionId: transactionId,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create a new monthly closing document
      await setDoc(monthlyClosingRef, {
        year: commissionData.year,
        month: commissionData.month,
        glTransactionId: transactionId,
        isClosed: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
    
    console.log(`Saved GL transaction ID ${transactionId} to monthly closing document ${monthlyClosingId}`);
    
    return transactionId;
  } catch (error) {
    console.error('Error creating monthly commission GL transaction:', error);
    throw error;
  }
};

/**
 * Check if a GL transaction already exists for a specific month
 * 
 * @param year Year
 * @param month Month (0-11)
 * @returns Promise with the GL transaction ID if exists, null otherwise
 */
export const checkMonthlyCommissionGLTransaction = async (
  year: number,
  month: number
): Promise<string | null> => {
  try {
    console.log(`Checking for GL transaction for ${year}-${month+1}`);
    
    // Method 1: Check the monthly closing document
    const monthlyClosingId = `${year}-${month + 1}`;
    const monthlyClosingRef = doc(db, 'monthlyClosings', monthlyClosingId);
    const monthlyClosingDoc = await getDoc(monthlyClosingRef);
    
    if (monthlyClosingDoc.exists()) {
      const monthlyClosingData = monthlyClosingDoc.data();
      if (monthlyClosingData.glTransactionId) {
        console.log(`Found GL transaction ID in monthly closing: ${monthlyClosingData.glTransactionId}`);
        return monthlyClosingData.glTransactionId;
      }
    }
    
    // Method 2: If not found in monthly closing, check GL transactions directly
    const referenceId = `${year}-${month + 1}`;
    const transactions = await getGLTransactions(
      undefined,
      undefined,
      'monthly_commission',
      referenceId
    );
    
    if (transactions.length > 0) {
      const transactionId = transactions[0].id;
      console.log(`Found GL transaction by querying transactions: ${transactionId}`);
      
      // Update the monthly closing document with the found transaction ID
      if (transactionId) {
        try {
          if (monthlyClosingDoc.exists()) {
            await updateDoc(monthlyClosingRef, {
              glTransactionId: transactionId,
              updatedAt: Timestamp.now()
            });
          } else {
            await setDoc(monthlyClosingRef, {
              year: year,
              month: month,
              glTransactionId: transactionId,
              isClosed: true,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            });
          }
          console.log(`Updated monthly closing document with transaction ID: ${transactionId}`);
        } catch (updateError) {
          console.error('Error updating monthly closing document:', updateError);
          // Continue even if update fails
        }
      }
      
      return transactionId || null;
    }
    
    console.log('No GL transaction found');
    return null;
  } catch (error) {
    console.error('Error checking monthly commission GL transaction:', error);
    throw error;
  }
};

/**
 * Get GL transaction details for preview before reversal
 * 
 * @param year Year
 * @param month Month (0-11)
 * @returns Promise with the GL transaction details or null if not found
 */
export const getMonthlyCommissionGLTransactionForReversal = async (
  year: number,
  month: number
): Promise<GLTransaction | null> => {
  try {
    console.log(`Getting GL transaction details for ${format(new Date(year, month), 'MMMM yyyy')}`);
    
    // Get the transaction ID from the monthly closing
    const transactionId = await checkMonthlyCommissionGLTransaction(year, month);
    
    if (!transactionId) {
      console.log(`No GL transaction found for ${format(new Date(year, month), 'MMMM yyyy')}`);
      return null;
    }
    
    console.log(`Found transaction ID: ${transactionId}`);
    
    // Get the transaction details
    const transaction = await getGLTransactionById(transactionId);
    
    if (!transaction) {
      console.log(`Transaction with ID ${transactionId} not found`);
      return null;
    }
    
    return transaction;
  } catch (error) {
    console.error('Error getting GL transaction details for reversal:', error);
    throw error;
  }
};

/**
 * Reverse a monthly commission GL transaction
 * 
 * @param year Year
 * @param month Month (0-11)
 * @returns Promise with void
 */
export const reverseMonthlyCommissionGLTransaction = async (
  year: number,
  month: number
): Promise<void> => {
  try {
    console.log(`Attempting to reverse GL transaction for ${format(new Date(year, month), 'MMMM yyyy')}`);
    
    // Get the transaction ID from the monthly closing
    const transactionId = await checkMonthlyCommissionGLTransaction(year, month);
    
    if (!transactionId) {
      throw new Error(`No GL transaction found for ${format(new Date(year, month), 'MMMM yyyy')}`);
    }
    
    console.log(`Found transaction ID to reverse: ${transactionId}`);
    
    // Import the deleteGLTransaction function
    const { deleteGLTransaction } = await import('../firebase/glTransactionServices');
    
    // Delete the GL transaction
    await deleteGLTransaction(transactionId);
    console.log(`Deleted GL transaction with ID: ${transactionId}`);
    
    // Update the monthly closing to remove the GL transaction ID
    const monthlyClosingId = `${year}-${month + 1}`;
    const monthlyClosingRef = doc(db, 'monthlyClosings', monthlyClosingId);
    
    // First check if the monthly closing document exists
    const monthlyClosingDoc = await getDoc(monthlyClosingRef);
    
    if (monthlyClosingDoc.exists()) {
      // Update the document to set glTransactionId to null
      await updateDoc(monthlyClosingRef, {
        glTransactionId: null,
        updatedAt: Timestamp.now()
      });
      console.log(`Updated monthly closing document ${monthlyClosingId} to remove GL transaction ID`);
    } else {
      console.log(`Monthly closing document ${monthlyClosingId} does not exist, no need to update`);
    }
    
    // Also call the helper function to ensure consistency
    await updateMonthlyClosingStatus(year, month, false, null);
    
    console.log(`Successfully reversed GL transaction for ${format(new Date(year, month), 'MMMM yyyy')}`);
  } catch (error) {
    console.error('Error reversing monthly commission GL transaction:', error);
    throw error;
  }
};

/**
 * Update monthly closing status
 * 
 * @param year Year
 * @param month Month (0-11)
 * @param isClosed Whether the month is closed
 * @param glTransactionId GL transaction ID (optional)
 */
export const updateMonthlyClosingStatus = async (
  year: number,
  month: number,
  isClosed: boolean,
  glTransactionId: string | null
): Promise<void> => {
  try {
    const monthlyClosingId = `${year}-${month + 1}`;
    const monthlyClosingRef = doc(db, 'monthlyClosings', monthlyClosingId);
    
    const updateData: any = {
      isClosed,
      updatedAt: Timestamp.now()
    };
    
    if (glTransactionId !== undefined) {
      updateData.glTransactionId = glTransactionId;
    }
    
    await updateDoc(monthlyClosingRef, updateData);
  } catch (error) {
    console.error('Error updating monthly closing status:', error);
    throw error;
  }
};

/**
 * Update monthly closing with GL transaction ID
 * 
 * @param year Year
 * @param month Month (0-11)
 * @param glTransactionId GL transaction ID
 */
export const updateMonthlyClosingWithGLTransaction = async (
  year: number,
  month: number,
  glTransactionId: string
): Promise<void> => {
  // This function would update the monthly closing record with the GL transaction ID
  // You'll need to implement the Firestore service for monthly closings
  console.log(`Updating monthly closing for ${year}-${month + 1} with GL transaction ID: ${glTransactionId}`);
  // TODO: Implement this when you have a monthly closing collection in Firestore
};
